/**
 * Header Commands Implementation for UI-Model-1
 *
 * Handles execute and undo operations for header configuration updates.
 */

import { UIModelState } from '../../types/state.types';
import { UpdateHeaderCommand } from '../../types/command.types';
import { HeaderConfigurationAPI } from '../../types/api.types';

/**
 * Execute UPDATE_HEADER command
 * Merges the updates into the current header configuration
 */
export const executeUpdateHeader = (
  state: UIModelState,
  command: UpdateHeaderCommand
): UIModelState => {
  const currentConfig = state.model?.layout.header?.configuration;
  if (!currentConfig || !state.model) {
    console.warn('Cannot update header: no header configuration found');
    return state;
  }

  // Store previous values for undo (mutate command for undo support)
  const previousValues: Partial<HeaderConfigurationAPI> = {};
  for (const key of Object.keys(command.payload.updates) as (keyof HeaderConfigurationAPI)[]) {
    previousValues[key] = currentConfig[key] as never;
  }
  command.payload.previousValues = previousValues;

  return {
    ...state,
    model: {
      ...state.model,
      layout: {
        ...state.model.layout,
        header: {
          ...state.model.layout.header,
          configuration: {
            ...currentConfig,
            ...command.payload.updates,
          },
        },
      },
    },
  };
};

/**
 * Undo UPDATE_HEADER command
 * Restores the previous header configuration values
 */
export const undoUpdateHeader = (
  state: UIModelState,
  command: UpdateHeaderCommand
): UIModelState => {
  const currentConfig = state.model?.layout.header?.configuration;
  if (!currentConfig || !state.model || !command.payload.previousValues) {
    console.warn('Cannot undo header update: missing state or previous values');
    return state;
  }

  return {
    ...state,
    model: {
      ...state.model,
      layout: {
        ...state.model.layout,
        header: {
          ...state.model.layout.header,
          configuration: {
            ...currentConfig,
            ...command.payload.previousValues,
          },
        },
      },
    },
  };
};
